/**
 * Removes all spaces and non alphanumeric chars from a given string,
 * the str will be returned in lowercase.
 *
 * @param { string } str
 * @returns Normalized string
 */
function createIDFrom(str = '') {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .replace(' ', '-')
    .replace(/[^a-zA-Z]+/g, '')
    .toLocaleLowerCase()
    .normalize('NFKD');
}

/**
 * Some elements can have invalid IDs as [object Object] or "xyz'e'"
 * This function informs if the given ID is valid or not for an element
 *
 * @param {string} id
 * @returns Boolean
 */
function isValidElementId(id = '') {
  if (!id || typeof id !== 'string') {
    return false;
  }

  return /^[A-Za-z][-A-Za-z0-9_:.]*$/.test(id);
}

/**
 * Verifies weather the current element has passed through the viewport.
 * Basically: if the element is or was already visible on the reading scroll.
 *
 * @param {HTMLElement} el
 * @param {HTMLElement} container
 * @returns Boolean
 */
function isSeenSection(el, container) {
  const rect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return rect.bottom <= containerRect.top + 200;
}

/**
 * Return and array with all the anchor element that represent
 * active (reading or alread read section according to the scroll).
 *
 * @returns Array<HTMLAnchorElement>
 */
function allCurrentActiveLinks() {
  return Array.from(document.querySelectorAll('.anchor-list-item .is-active-link'));
}

/**
 * Listens to the scroll event for the querySelector resultant element
 * in the scrollSelector parameter, and updates the anchor list on the menu
 * about correct items and activation status.
 *
 * @param {Array<HTMLHeadingElement>} elements
 * @param {HTMLElement} scrollSelector
 */
function handleScroll(elements, scrollSelector) {
  if (typeof window !== 'undefined') {
    const scrollable = document.querySelector(scrollSelector);

    if (window.$$dxHeadingCheck) {
      scrollable.removeEventListener('scroll', window?.$$dxHeadingCheck);
    }

    window.$$dxHeadingCheck = () => {
      try {
        elements.forEach(item => {
          const sectionSeen = isSeenSection(item, scrollable);
          const a = document.querySelector(`.anchor-list-item a[href="#${item.id}"]`);
          a?.classList[sectionSeen ? 'add' : 'remove']('is-active-link');
        });

        allCurrentActiveLinks()
          .slice(0, -1)
          .forEach(active => active.classList.remove('is-active-link'));
      } catch (error) {
        console.warn(error);
      }
    };

    scrollable.addEventListener('scroll', window?.$$dxHeadingCheck);
  }
}

/**
 * Search for headings on a given container selector and return them as an object
 * collection containing the item.id and item.label for each heading.
 *
 * @param { h, maxItems, from } options {
 *   h: [2], // heading levels to consider (1 to 6)
 *   maxItems: 15, // max of returned items
 *   scrollSelector: '#yzx' // the element that must be scrolled
 *   from: '.xyz' // selector from where to search for the headings
 * }
 *
 * @returns Array<{ id: string, label: string }>
 */
function getHeadingsInfo(options = {}) {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const { h, from, maxItems, scrollSelector } = options;

    const elements = h.reduce((results, level) => {
      try {
        const selector = `${from} h${level}`;
        const collected = Array.from(document.querySelectorAll(selector));
        return results.concat(collected);
      } catch (error) {
        console.warn(error);
        return results;
      }
    }, []);

    handleScroll(elements, scrollSelector);

    const list = elements.map(item => {
      if (!item.id || !isValidElementId(item.id)) {
        item.id = createIDFrom(item.textContent);
      }

      return {
        id: item.id,
        label: item.textContent,
      };
    });

    return list.slice(0, maxItems);
  } catch (error) {
    console.error(error);
    return [];
  }
}

/**
 * Scroll back the given "selector" element after 100ms.
 * The amout off scroll will be the header size in px + 24px.
 *
 * @returns void
 */
function scrollRecoil() {
  if (typeof window === 'undefined') {
    return;
  }

  setTimeout(() => {
    const padding = 24;
    const selector = '#dev-main';
    const headerSize = document.querySelector('.navbar-container')?.offsetHeight || 0;
    const scrollable = document.querySelector(selector) || document.body;

    scrollable.scroll({
      behavior: 'smooth',
      top: (scrollable.scrollTop) - (headerSize + padding),
    });
  }, 100);
}

module.exports = {
  scrollRecoil,
  getHeadingsInfo,
};
